import { logger } from '../utils/logger';
import axios, { InternalAxiosRequestConfig, AxiosResponse } from 'axios';

// For local development
const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
// Check if we're in development mode
const isDev = process.env.NODE_ENV === 'development' || import.meta.env.DEV;

// In development, use API proxy
// In production, use the configured API URL
const apiBaseUrl = isLocalDev
  ? (isDev ? '/api' : 'http://backend:8000')
  : (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '/api');

// Create the axios instance with baseURL
export const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Enhanced request logging for development
if (isDev) {
  api.interceptors.request.use(config => {
    const url = config.url || '';
    const method = config.method?.toUpperCase() || 'GET';
    const baseURL = config.baseURL || '';

    logger.log(`🚀 API Request: ${method} ${url} (BaseURL: ${baseURL})`);

    return config;
  });
}

// Add a request interceptor to include the auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Enhanced response handling with error management
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (isDev) {
      logger.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // If connection refused or server not available
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      logger.error(`⚠️ API connection error: ${error.message}`);

      if (isDev) {
        logger.warn('👉 Backend server may not be running. Check your backend service.');
      }
    }

    // Log and handle API errors
    logger.error('API Error:', error);
    
    // Handle specific error codes
    if (error.response) {
      // Handle 401 Unauthorized errors
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
      
      // Handle 500 server errors with more details
      if (error.response.status >= 500) {
        logger.error('Server error details:', error.response.data);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
